import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Star, Info } from "lucide-react";
import { Seo } from "../components/Seo";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/motion";
import Marquee from "../components/Marquee";
import { BUSINESS, MENU_CATEGORIES, ALL_ITEMS, PRICE_NOTE, restaurantLd, breadcrumbLd } from "../data/business";

const TABS = [{ id: "semua", title: "Semua" }, ...MENU_CATEGORIES.map((c) => ({ id: c.id, title: c.title }))];

export default function MenuPage() {
  const [tab, setTab] = useState("semua");
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MENU_CATEGORIES.map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (i) =>
          (tab === "semua" || cat.id === tab) &&
          (!q || i.name.toLowerCase().includes(q) || i.desc.toLowerCase().includes(q))
      ),
    })).filter((c) => c.items.length > 0);
  }, [tab, query]);

  const noResult = visible.length === 0 && ALL_ITEMS.length > 0;

  return (
    <>
      <Seo
        title="Menu Bakso Tujuh Pemuda — Bakso, Mie Yamin & Harga"
        description="Daftar menu Bakso Tujuh Pemuda: bakso halus, urat, daging, dan telur (Rp 27–39 ribu), mie yamin ala Bandung, mie ayam, bakso goreng, es campur, dan es cendol."
        path="/menu/"
        jsonLd={[restaurantLd, breadcrumbLd([{ name: "Beranda", path: "/" }, { name: "Menu", path: "/menu/" }])]}
      />
      <PageHeader
        crumb="Menu"
        eyebrow="Diracik fresh setelah dipesan"
        lines={[<>Menu <span className="italic font-semibold text-terracotta">Bakso Tujuh Pemuda</span></>]}
      />
      <section className="py-16 sm:py-20" data-testid="menu-page">
        <div className="max-w-6xl mx-auto px-4">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Kategori menu">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={tab === t.id}
                    data-testid={`menu-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`px-4 py-2 rounded-full text-sm font-bold transition-colors duration-300 ${
                      tab === t.id ? "bg-terracotta text-cream" : "bg-tint border border-line text-ink-soft hover:text-terracotta"
                    }`}
                  >
                    {t.title}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cari menu…"
                  aria-label="Cari menu"
                  data-testid="menu-search-input"
                  className="w-full lg:w-72 pl-10 pr-4 py-2.5 rounded-full bg-white border border-line text-sm focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
              </div>
            </div>
          </Reveal>

          {noResult && (
            <p className="mt-16 text-center text-ink-soft" data-testid="menu-empty-result">
              Tidak ada menu yang cocok dengan pencarian “{query}”.
            </p>
          )}

          {visible.map((cat) => (
            <div key={cat.id} className="mt-14" data-testid={`menu-category-${cat.id}`}>
              <Reveal>
                <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl">{cat.title}</h2>
                <p className="mt-2 text-sm text-ink-soft">{cat.note}</p>
              </Reveal>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {cat.items.map((item, i) => (
                  <Reveal key={item.id} delay={i * 0.05}>
                    <article
                      data-testid={`menu-card-${item.id}`}
                      className="h-full bg-white rounded-2xl border border-line p-5 hover:border-terracotta/50 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-display font-bold text-lg flex items-center gap-2">
                          {item.name}
                          {item.favorite && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-sans font-bold uppercase tracking-widest text-amberbrand">
                              <Star size={11} fill="currentColor" /> Favorit
                            </span>
                          )}
                        </h3>
                        <span className="font-display font-black text-terracotta whitespace-nowrap">{item.price}</span>
                      </div>
                      <p className="mt-2 text-sm text-ink-soft leading-relaxed">{item.desc}</p>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}

          <Reveal>
            <div className="mt-14 flex items-start gap-3 rounded-2xl bg-tint border border-line p-5 text-sm text-ink-soft" data-testid="menu-price-note">
              <Info size={18} className="shrink-0 mt-0.5 text-amberbrand" />
              <p>
                {PRICE_NOTE} Setiap mangkuk bakso disajikan dengan tetelan goreng serta pilihan bihun atau kwetiau.
                Tersedia juga sambal dengan pedas yang kuat. Sampaikan ke kami lewat{" "}
                <a href={BUSINESS.links.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="menu-whatsapp-link" className="font-bold text-terracotta hover:underline">
                  WhatsApp
                </a>{" "}
                atau lihat{" "}
                <Link to="/lokasi/" data-testid="menu-location-link" className="font-bold text-terracotta hover:underline">
                  lokasi kami
                </Link>{" "}
                untuk makan di tempat.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      <Marquee dark />
    </>
  );
}
